/**
 * iter103 (Priority 3) France dossier prototype.
 *
 * Redesign of the country detail page for one country only (France, largest
 * cohort at 16 students). Purpose: prove a new information architecture
 * before touching the other 18 dossiers.
 *
 * Problems in the current design:
 *   · Too many cards stacked on one long scroll — no clear hierarchy.
 *   · Everything shown at once, no progressive disclosure.
 *   · Navigation is scroll only, no anchor bar or tab pattern.
 *
 * This prototype:
 *   · Sticky sub-nav with four sections (Overview, Visa, Living, Peers).
 *   · One panel visible at a time. Reduces simultaneous competition.
 *   · Progressive disclosure inside each panel (checklist collapsible,
 *     consular list defaults to "your region" expanded, others closed).
 *   · Compact top hero with the flag, visa type, and portal CTA on one row.
 *   · Compact driving/rental card (iter103 Priority 2) baked into Living.
 *   · No em dash anywhere in copy.
 *   · "Last updated Aug 26, 2026" everywhere a stamp appears.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AlertTriangle, ArrowLeft, ArrowUpRight, CalendarClock, Check,
  ChevronDown, ChevronUp, Clock, ExternalLink, Info, Landmark, MessageCircle,
  Train, Wallet,
} from "lucide-react";
import CountryPostArrival from "@/components/CountryPostArrival";
import WaitTimePanel from "@/components/WaitTimePanel";
import InrAmount from "@/components/InrAmount";
import DrivingCard from "@/components/DrivingCard";
import { flagFor } from "@/lib/flags";
import { useFxInr } from "@/hooks/useFxInr";

const LAST_UPDATED = "Aug 26, 2026";

const SECTIONS = [
  { k: "overview", label: "Overview" },
  { k: "visa",     label: "Visa" },
  { k: "living",   label: "Living" },
  { k: "peers",    label: "Peers" },
];

export default function CountryDetailV2() {
  // iter104 — Promoted from a France-only prototype to the shared dossier
  // layout after founder approval. The path param drives the country now;
  // all 19 dossiers render through this same component.
  const { country } = useParams();
  const [pb, setPb] = useState(null);
  const [schools, setSchools] = useState([]);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get(`/countries/${encodeURIComponent(country)}`)
      .then(r => setPb(r.data))
      .catch(() => setErr(true));
    api.get(`/countries/${encodeURIComponent(country)}/bschools`)
      .then(r => setSchools(r.data.bschools || []))
      .catch(() => {});
  }, [country]);

  useEffect(() => {
    // Sync deep-link `?section=visa` on landing so we can point students
    // straight at the visa checklist from Compass.
    const params = new URLSearchParams(window.location.search);
    const s = params.get("section");
    if (s && SECTIONS.some(x => x.k === s)) setTab(s);
  }, []);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Link to="/countries" className="text-muted text-sm">← Back</Link>
      <div className="mt-4 text-ink">Country not found.</div>
    </div>
  );
  if (!pb) return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="moe-shimmer h-8 w-64 mb-4"/>
      <div className="moe-shimmer h-4 w-96"/>
    </div>
  );

  const schoolNames = [...new Set(schools.map(s => s.bschool_short))];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24" data-testid="country-detail-v2" data-country={pb.country_name}>
      <Link to="/countries" className="inline-flex items-center gap-1 text-sm text-muted hover:text-elec">
        <ArrowLeft size={14}/> All countries
      </Link>

      {/* ---------- Compact hero row ---------- */}
      <Hero pb={pb} />

      {/* ---------- Sticky sub-nav ---------- */}
      <div className="mt-6 sticky top-16 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-paper/90 backdrop-blur border-b border-ink/10">
        <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="france-tabs">
          {SECTIONS.map((s) => (
            <button
              key={s.k}
              onClick={() => setTab(s.k)}
              data-testid={`france-tab-${s.k}`}
              className={"px-3 sm:px-4 py-3 text-[13px] sm:text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap " +
                (tab === s.k
                  ? "border-elec text-ink"
                  : "border-transparent text-muted hover:text-ink")}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------- Panels ---------- */}
      <div className="mt-6">
        {tab === "overview" && <Overview pb={pb} schoolNames={schoolNames} onJump={setTab} />}
        {tab === "visa"     && <VisaPanel pb={pb} />}
        {tab === "living"   && <LivingPanel pb={pb} schoolNames={schoolNames} />}
        {tab === "peers"    && <PeersPanel pb={pb} />}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Hero({ pb }) {
  return (
    <div className="mt-5 grid md:grid-cols-5 gap-5 items-start">
      <div className="md:col-span-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted font-bold">Dossier</div>
        <h1 className="mt-1.5 text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[0.98] flex items-center gap-2.5">
          <span aria-hidden="true" className="text-3xl md:text-4xl leading-none" data-testid={`flag-${pb.country_name}`}>
            {flagFor(pb.country_name)}
          </span>
          {pb.country_name}
        </h1>
        <div className="mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className="text-[15px] font-semibold text-ink">{pb.visa_type}</div>
          <div className="text-[11px] text-muted">Last updated {LAST_UPDATED}</div>
        </div>
      </div>
      <div className="md:col-span-2 moe-panel-elec p-4 md:p-5 text-white rounded-2xl">
        <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 font-bold">Book your appointment</div>
        <div className="mt-1.5 text-[13px] opacity-90">{pb.processing_time || "Check portal for current wait"}</div>
        {pb.booking_url && (
          <a href={pb.booking_url} target="_blank" rel="noreferrer"
             className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white text-ink font-semibold text-[13px] hover:bg-white/90 transition-colors">
            Open portal <ExternalLink size={13}/>
          </a>
        )}
      </div>
    </div>
  );
}

/* ---------- OVERVIEW ---------- */
function Overview({ pb, schoolNames, onJump }) {
  return (
    <div className="space-y-5" data-testid="france-overview">
      {pb.recent_flag && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700"/>
          <div className="text-[13px] leading-snug text-ink">{pb.recent_flag}</div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Wallet,        l: "Visa fee",        v: pb.visa_fee_full || pb.visa_fee?.split(",")[0].trim() || "–" },
          { icon: Wallet,        l: "Proof of funds",  v: pb.proof_of_funds_amount || "–" },
          { icon: CalendarClock, l: "Processing",      v: pb.processing_time || "–" },
          { icon: Clock,         l: "Apply before",    v: pb.apply_before_days ? `${pb.apply_before_days} days` : "–" },
        ].map(s => (
          <div key={s.l} className="rounded-2xl border border-ink/10 p-3.5 bg-white">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted font-bold">
              <s.icon size={11} className="text-elec"/>{s.l}
            </div>
            <div className="mt-1.5 text-[16px] font-bold leading-tight">{s.v}</div>
            {(s.l === "Visa fee" || s.l === "Proof of funds") && (
              <InrAmount raw={s.v} size="xs" showRateLine={false} />
            )}
          </div>
        ))}
      </div>
      <InrFxCaption pb={pb} />

      {/* Preview strip. Two rails that jump into the deeper tabs. Keeps
          Overview scannable without repeating full checklist content. */}
      <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="overview-jump-rails">
        <button
          type="button"
          onClick={() => onJump("visa")}
          data-testid="jump-visa"
          className="rounded-2xl border border-ink/10 bg-white p-4 text-left hover:border-elec transition-colors"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Visa track</div>
          <div className="mt-1.5 text-[14px] font-bold text-ink">
            {pb.official_checklist?.length || 0} document checklist, {pb.consular_jurisdictions?.length || 0} consular centers, wait times
          </div>
          <div className="mt-1 text-[11.5px] text-muted">Open visa track <ArrowUpRight size={10} className="inline"/></div>
        </button>
        <button
          type="button"
          onClick={() => onJump("living")}
          data-testid="jump-living"
          className="rounded-2xl border border-ink/10 bg-white p-4 text-left hover:border-elec transition-colors"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Living track</div>
          <div className="mt-1.5 text-[14px] font-bold text-ink">
            Post-arrival rails, driving and rental, {schoolNames.length} partner schools
          </div>
          <div className="mt-1 text-[11.5px] text-muted">Open living track <ArrowUpRight size={10} className="inline"/></div>
        </button>
      </div>
    </div>
  );
}

/* ---------- VISA ---------- */
function VisaPanel({ pb }) {
  const [checkOpen, setCheckOpen] = useState(true);
  return (
    <div className="space-y-6" data-testid="france-visa">
      {pb.consular_jurisdiction_note && (
        <div className="rounded-2xl border-2 border-[color:var(--moe-yolk)] bg-[color:var(--moe-yolk)]/15 px-4 py-3 flex items-start gap-2.5"
             data-testid="long-stay-note">
          <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
          <div className="text-[13px] leading-snug">{pb.consular_jurisdiction_note}</div>
        </div>
      )}

      {/* Consular list. Accordion, closed by default. */}
      {pb.consular_jurisdictions?.length > 0 && (
        <div className="rounded-2xl border border-ink/10 bg-white p-4 md:p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Consular jurisdiction, India</div>
          <div className="mt-1 text-[15px] font-bold text-ink">
            Which center covers your home state
          </div>
          <div className="mt-4 space-y-1.5" data-testid="consular-jurisdiction-list">
            {pb.consular_jurisdictions.map((j) => (
              <JurisdictionRow key={j.region_name} j={j}/>
            ))}
          </div>
        </div>
      )}

      {/* Checklist. Collapsible. */}
      {pb.official_checklist?.length > 0 && (
        <div className="rounded-2xl border border-ink/10 bg-white">
          <button
            type="button"
            onClick={() => setCheckOpen(o => !o)}
            data-testid="france-checklist-toggle"
            className="w-full px-4 py-3.5 md:px-5 flex items-center justify-between"
          >
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Official checklist</div>
              <div className="mt-0.5 text-[15px] font-bold text-ink">
                {pb.official_checklist.length} documents you must produce
              </div>
            </div>
            {checkOpen ? <ChevronUp size={16} className="text-elec"/> : <ChevronDown size={16} className="text-elec"/>}
          </button>
          {checkOpen && (
            <ul className="border-t border-ink/10 px-4 md:px-5 py-4 grid md:grid-cols-2 gap-x-6 gap-y-2.5">
              {pb.official_checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-ink leading-snug">
                  <span className="mt-0.5 w-4 h-4 rounded-full border border-ink flex items-center justify-center shrink-0">
                    <Check size={9} strokeWidth={3} className="text-ink"/>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
          {pb.source_url && (
            <div className="border-t border-ink/10 px-4 md:px-5 py-2.5 text-[11px] text-muted flex items-center gap-1.5">
              Sourced from{" "}
              <a href={pb.source_url} target="_blank" rel="noreferrer" className="text-elec hover:underline">
                {pb.source_url.replace(/^https?:\/\//,"").split("/")[0]}
              </a>
              <ExternalLink size={10}/>
            </div>
          )}
        </div>
      )}

      <WaitTimePanel country={pb.country_name}/>

      {pb.visa_fee_note && (
        <div className="flex items-start gap-2 text-[12px] text-muted p-3 rounded-lg bg-tint" data-testid="visa-fee-note">
          <Info size={13} className="mt-0.5 shrink-0 text-elec"/>
          <div className="leading-snug">{pb.visa_fee_note}</div>
        </div>
      )}
    </div>
  );
}

function JurisdictionRow({ j }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-ink/10 rounded-xl bg-white" data-testid={`jurisdiction-${(j.region_name || "").toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
              className="w-full px-3.5 py-2.5 flex items-center gap-2.5 text-left">
        <Landmark size={13} className="text-elec shrink-0"/>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-ink truncate">{j.region_name}</div>
          {j.consulate_or_vfs_office && !open && (
            <div className="mt-0.5 text-[11px] text-muted truncate">{j.consulate_or_vfs_office}</div>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-elec shrink-0"/> : <ChevronDown size={14} className="text-elec shrink-0"/>}
      </button>
      {open && (
        <div className="border-t border-ink/10 px-3.5 py-3 space-y-2">
          {j.consulate_or_vfs_office && (
            <div className="text-[12px] text-ink/80 leading-snug">{j.consulate_or_vfs_office}</div>
          )}
          {j.indian_states_covered?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Covers</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {j.indian_states_covered.map((s) => (
                  <span key={s} className="text-[10.5px] font-semibold px-1.5 py-0.5 border border-ink/20 rounded">{s}</span>
                ))}
              </div>
            </div>
          )}
          {j.note && (
            <div className="text-[11px] text-ink/70 italic leading-snug">{j.note}</div>
          )}
          {j.booking_url && (
            <a href={j.booking_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-elec hover:underline">
              Book at this center <ExternalLink size={10}/>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- LIVING ---------- */
function LivingPanel({ pb, schoolNames }) {
  return (
    <div className="space-y-6" data-testid="france-living">
      {schoolNames.length > 0 && (
        <div className="rounded-2xl border border-ink/10 bg-emerald-500/[0.04] p-4 md:p-5">
          <div className="flex items-baseline justify-between mb-2.5 gap-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
              Indian B-schools with partners here
            </div>
            <div className="text-[11px] text-muted font-semibold shrink-0">
              {schoolNames.length} schools
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {schoolNames.map(s => (
              <Link key={s} to={`/universities/${encodeURIComponent(s)}`}
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-full bg-white border border-ink/15 hover:border-elec hover:text-elec transition-colors">
                {s} <ArrowUpRight size={10}/>
              </Link>
            ))}
          </div>
        </div>
      )}

      <DrivingCard driving={pb.driving} countryName={pb.country_name}/>

      <CountryPostArrival country={pb.country_name}/>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-ink/10 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Notable universities</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(pb.notable_universities || []).map(u => (
              <span key={u} className="moe-chip text-[11px]">{u}</span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-ink/10 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Major cities and train app</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(pb.major_cities || []).map(c => (
              <span key={c} className="moe-chip text-[11px]">{c}</span>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-muted">
            <Train size={11} className="inline mr-1"/>{pb.train_app}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- PEERS ---------- */
function PeersPanel({ pb }) {
  return (
    <div className="space-y-4" data-testid="france-peers">
      <div className="rounded-2xl border border-elec/25 bg-elec/[0.04] p-5 md:p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Cohort</div>
        <div className="mt-1.5 text-[17px] font-bold text-ink leading-tight">
          Every peer heading to {pb.country_name} the same term as you.
        </div>
        <div className="mt-2 text-[13px] text-ink/70 leading-snug">
          Matches surface passively the moment your case has a host university and start date. No opt in required to see who is going.
        </div>
        <Link to="/dashboard/manifest?tab=matches"
              data-testid="peers-open-manifest"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-elec text-white font-semibold text-[13px] hover:bg-elec/90">
          <MessageCircle size={13}/> Open Cohort
        </Link>
      </div>
    </div>
  );
}

/* ---------- FX caption (same as legacy) ---------- */
function InrFxCaption({ pb }) {
  const { rates, asOf, ready, available } = useFxInr();
  if (!ready || !available || !rates) return null;
  const blob = `${pb?.visa_fee_full || ""} ${pb?.visa_fee || ""} ${pb?.proof_of_funds_amount || ""}`.toUpperCase();
  const codes = ["EUR", "CHF", "NOK", "SEK", "DKK", "PLN", "HUF", "CZK", "GBP"].filter(c => blob.includes(c));
  if (!codes.length) return null;
  const _fmt = (n) => "\u20B9" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let asOfLabel = asOf;
  try {
    asOfLabel = new Date(asOf + "T00:00:00Z").toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch {}
  const primary = codes[0];
  const primarySymbol = { EUR: "\u20AC", GBP: "\u00A3", CHF: "CHF", NOK: "kr", SEK: "kr", DKK: "kr", PLN: "z\u0142", HUF: "Ft", CZK: "K\u010D" }[primary] || primary;
  return (
    <div data-testid="fx-caption" className="mt-1 text-[11px] text-muted">
      Live rate from ECB via frankfurter.app, as of {asOfLabel}.{" "}
      <span className="text-ink">{primarySymbol}1 = {_fmt(rates[primary])}</span>
      {codes.length > 1 && (
        <span>. {codes.slice(1).map((c) => (
          <span key={c}>{c} 1 = {_fmt(rates[c])} </span>
        ))}</span>
      )}
    </div>
  );
}
